import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { timeAgo } from "../NotificationsBell";
import { askOlliPath } from "../../lib/olli";
import { useAskOlli } from "../../../hooks/useFeatureFlags";

/**
 * Recent prompts under an Olli prompt box (design: components/assistant/RecentPrompts.tsx).
 * Prompts are kept in this browser, per prompt box. "All chats" opens Ask Olli;
 * "Open chat" opens the Ask Olli chat a prompt started, shown only when it
 * started one (the Home box sends to Olli, which records the chat id; the
 * campaign, post and automation boxes fill their own forms and have no chat).
 */

const KEY = (surface) => `olli:recent-prompts:${surface}`;
const read = (surface) => {
  try { return JSON.parse(localStorage.getItem(KEY(surface)) || "[]"); } catch (_) { return []; }
};

/** Remember a prompt the clinic sent on this surface; `chatId` = the Ask Olli chat it started, if any. */
export function logPrompt(surface, prompt, chatId = null) {
  const text = (prompt || "").trim();
  if (!text || !surface) return;
  try {
    const prev = read(surface);
    const known = prev.find((p) => p.prompt === text)?.chatId ?? null;
    const next = [{ prompt: text, at: Date.now(), chatId: chatId ?? known }, ...prev.filter((p) => p.prompt !== text)].slice(0, 20);
    localStorage.setItem(KEY(surface), JSON.stringify(next));
    window.dispatchEvent(new Event(`olli:prompts:${surface}`));
  } catch (_) { /* storage blocked: just don't remember */ }
}

export function RecentPrompts({ surface, onPick }) {
  const navigate = useNavigate();
  const askOlliOn = useAskOlli();
  const [items, setItems] = useState(() => read(surface));
  useEffect(() => {
    const refresh = () => setItems(read(surface));
    window.addEventListener(`olli:prompts:${surface}`, refresh);
    return () => window.removeEventListener(`olli:prompts:${surface}`, refresh);
  }, [surface]);
  const list = items.slice(0, 4);
  if (!list.length) return null;

  return <section className="mt-4 border-t border-line pt-3" aria-label="Recent prompts">
    <div className="mb-1 flex items-center justify-between gap-3">
      <h3 className="text-caption font-semibold text-muted">Recent prompts</h3>
      {askOlliOn && <button type="button" onClick={() => navigate(askOlliPath())} className="text-caption font-semibold text-sky-deep hover:underline">All chats</button>}
    </div>
    <ul className="divide-y divide-line">
      {list.map((item) => (
        <li key={`${item.at}-${item.prompt}`} className="flex items-center gap-3 py-2">
          <button type="button" onClick={() => onPick(item.prompt)} className="min-w-0 flex-1 truncate text-left text-small hover:text-sky-deep">{item.prompt}</button>
          <span className="shrink-0 text-caption text-muted">{timeAgo(item.at)}</span>
          {askOlliOn && item.chatId && <button type="button" onClick={() => navigate(askOlliPath(item.chatId))} className="shrink-0 text-caption font-semibold text-sky-deep hover:underline">Open chat</button>}
        </li>
      ))}
    </ul>
  </section>;
}
